'use client';

import { type ReactElement, useState } from 'react';
import { Activity, Complete, Karma, Visualization } from '@doist/icons/brand';
import { Quote } from '@doist/marketist';
import { FeatureRadioGroup } from 'components/feature-radio-tile/feature-radio-group';
import { RadioTileScene, SceneWrapper } from 'components/feature-radio-tile/scene-images';
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context';
import { ActivityHistoryScene } from 'components/product-ui/scenes/activity-history';
import { CompletedTasksScene } from 'components/product-ui/scenes/completed-tasks';
import { KarmaScene } from 'components/product-ui/scenes/karma';
import { ProductivityVisualizationsScene } from 'components/product-ui/scenes/productivity-visualizations';
import { ScrollHeader } from 'components/scrolling-section/scroll-header/scroll-header';
import { ScrollSection } from 'components/scrolling-section/scroll-section/scroll-section';
import { useTranslation } from 'next-i18next';
import { useLocalisedContent } from 'services/hooks/use-localised-data';
import { quoteIvan } from './quote-map';
const radioTiles = ['visualizations', 'activityHistory', 'completedTasks', 'todoistKarma'] as const;
type RadioTile = (typeof radioTiles)[number];
export function SeeProgress() {
  const {
    t
  } = useTranslation('features-teams');
  const avatarSrc = useLocalisedContent(quoteIvan);
  const [activeValue, setActiveValue] = useState<RadioTile>('visualizations');
  function featureRadioTileChangeHandler(value: RadioTile) {
    setActiveValue(value);
  }
  const sceneImages: { [key in RadioTile]: string | ReactElement } = {
    visualizations: <ProductivityVisualizationsScene />,
    activityHistory: <ActivityHistoryScene />,
    completedTasks: <CompletedTasksScene />,
    todoistKarma: <KarmaScene />
  };
  return <ProductUIThemeProvider theme="yellow" data-sentry-element="ProductUIThemeProvider" data-sentry-component="SeeProgress" data-sentry-source-file="see-progress.tsx">
            <ScrollSection data-sentry-element="ScrollSection" data-sentry-source-file="see-progress.tsx">
                <ScrollSection.StickySection data-sentry-element="unknown" data-sentry-source-file="see-progress.tsx">
                    <SceneWrapper scene={sceneImages[activeValue]} data-sentry-element="SceneWrapper" data-sentry-source-file="see-progress.tsx" />
                </ScrollSection.StickySection>
                <ScrollSection.StaticSection data-sentry-element="unknown" data-sentry-source-file="see-progress.tsx">
                    <ScrollHeader title={t('seeProgress')} spotIllustration={{
          src: '/static/features-teams/features_spot6.png',
          offset: {
            x: -61,
            y: -47
          },
          mobileOffset: {
            x: -34,
            y: -34
          }
        }} data-sentry-element="ScrollHeader" data-sentry-source-file="see-progress.tsx">
                        {t('smallSteps')}
                    </ScrollHeader>

                    <FeatureRadioGroup<RadioTile> value={activeValue} onChange={featureRadioTileChangeHandler} namespace="features-teams" className="md-mbe-192 mbe-96" radioTiles={[{
          titleKey: 'visualizations',
          bodyKey: 'visualizationsBody',
          icon: <Visualization />,
          scene: <RadioTileScene scene={sceneImages.visualizations} />
        }, {
          titleKey: 'activityHistory',
          bodyKey: 'activityHistoryBody',
          icon: <Activity />,
          scene: <RadioTileScene scene={sceneImages.activityHistory} />
        }, {
          titleKey: 'completedTasks',
          bodyKey: 'completedTasksBody',
          icon: <Complete />,
          scene: <RadioTileScene scene={sceneImages.completedTasks} />
        }, {
          titleKey: 'todoistKarma',
          bodyKey: 'todoistKarmaBody',
          icon: <Karma />,
          scene: <RadioTileScene scene={sceneImages.todoistKarma} />
        }]} data-sentry-element="FeatureRadioGroup" data-sentry-source-file="see-progress.tsx" />

                    <ScrollSection.StickyAnchor data-sentry-element="unknown" data-sentry-source-file="see-progress.tsx">
                        <Quote quoteText={t('quoteIvan.quote')} author={t('quoteIvan.name')} role={t('quoteIvan.role')} avatarSrc={`/static/features-teams/avatars/${avatarSrc}`} data-sentry-element="Quote" data-sentry-source-file="see-progress.tsx" />
                    </ScrollSection.StickyAnchor>
                </ScrollSection.StaticSection>
            </ScrollSection>
        </ProductUIThemeProvider>;
}